import { BlankSlate } from "@msuite/ui/blank-slate";
import { useAutoAnimate } from "@msuite/ui/libs/auto-animate";
import { parseAsStringLiteral, useQueryState } from "@msuite/ui/libs/nuqs";
import { filter, guardArray } from "@msuite/utils/array";
import type { AbsenceListItem } from "@msuite/utils/types";
import { Fragment, useCallback, useMemo, useState } from "react";
import { AbsenceInboxCard } from "@/components/cards/absence-inbox-card";
import { useAbsences } from "@/hooks/use-absences";
import { AbsencesInboxCategories } from "./absences-inbox-categories";
import { AbsencesInboxMenu } from "./absences-inbox-menu";
import { AbsencesSearch } from "./absences-search";
import { type AbsenceState, type AbsencesSortBy, categoryValues } from "./shared";

/** Props Interface */
interface AbsencesInboxProps {
	searchValue: string;
	onSearch: (value: string) => void;
}

export function AbsencesInbox({ searchValue, onSearch }: AbsencesInboxProps) {
	/** Hooks */
	const [sortBy, setSortBy] = useState<AbsencesSortBy>("startDate");
	const [absenceState, setAbsenceState] = useState<AbsenceState>("all");
	const [sortByDirection, setSortByDirection] = useState<"asc" | "desc">("asc");
	const [onlyUpcoming, setOnlyUpcoming] = useState(false);

	const [category, setCategory] = useQueryState(
		"absence-inbox-category",
		parseAsStringLiteral(categoryValues).withDefault("all"),
	);
	const [parent] = useAutoAnimate();
	const { absences, refetch, isRefetching } = useAbsences();

	const handleOnSortBy = useCallback(
		(absences: Array<Omit<AbsenceListItem, "consumedDays">>) => {
			const sorted = [...absences].sort((a, b) => {
				const aValue = a[sortBy];
				const bValue = b[sortBy];

				if (aValue == null) return 1;
				if (bValue == null) return -1;

				if (aValue < bValue) return sortByDirection === "asc" ? -1 : 1;
				if (aValue > bValue) return sortByDirection === "asc" ? 1 : -1;
				return 0;
			});

			return sorted;
		},
		[sortBy, sortByDirection],
	);

	const handleOnFilter = useCallback(
		(absences: Array<Omit<AbsenceListItem, "consumedDays">>) => {
			let filtered = absences;
			if (absenceState !== "all") {
				filtered = filtered.filter((absence) => absence.state === absenceState);
			}
			if (onlyUpcoming) {
				const now = new Date();
				filtered = filtered.filter((absence) => new Date(absence.startDate) > now);
			}
			return filtered;
		},
		[absenceState, onlyUpcoming],
	);

	const filteredAbsences = useMemo(() => {
		let textSearchValues = filter(guardArray(absences), searchValue);
		if (category !== "all") {
			textSearchValues = textSearchValues.filter((absence) => absence.category === category);
		}
		textSearchValues = handleOnFilter(textSearchValues);
		textSearchValues = handleOnSortBy(textSearchValues);
		return textSearchValues;
	}, [absences, searchValue, category, sortBy, absenceState, sortByDirection, onlyUpcoming]);

	/** Render */
	return (
		<Fragment>
			<div className="w-full h-full v-stack p-1 min-h-0">
				<div className="h-stack">
					<AbsencesInboxCategories
						category={category}
						setCategory={setCategory}
					/>
					<div className="w-10" />
					<AbsencesInboxMenu
						onRefetch={refetch}
						isRefetching={isRefetching}
						sortBy={sortBy}
						setSortBy={setSortBy}
						absenceState={absenceState}
						setAbsenceState={setAbsenceState}
						sortByDirection={sortByDirection}
						setSortByDirection={setSortByDirection}
						onlyUpcoming={onlyUpcoming}
						setOnlyUpcoming={setOnlyUpcoming}
					/>
				</div>
				<AbsencesSearch onSearch={onSearch} />
				{guardArray(filteredAbsences).length > 0 ? (
					<div
						className="overflow-y-auto v-stack flex-1 min-h-0 pb-24"
						ref={parent}
					>
						{guardArray(filteredAbsences).map((absence) => (
							<AbsenceInboxCard
								key={absence.id}
								absence={absence}
							/>
						))}
					</div>
				) : (
					<BlankSlate />
				)}
			</div>
		</Fragment>
	);
}
